import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import { maxManagementReportDateRangeMs } from 'constants/AppConstants';
import { GroupingLevel } from 'api/model';

/**
 * Delad plumbing för de operativa nyckeltalen (hotellnätter, FT utan jobb, nekade jobb).
 *
 * Samma filter som övriga dashboarden, men taket är ett år i stället för 60 dagar: måtten ska
 * kunna följas per vecka över ett kvartal, och de räknas på uppdrag och event — inte på
 * prisuppslagningen som gör TB dyr.
 */
const useOperationsKpiFilter = () => {
  const {
    dashboardState: {
      daterange,
      selectedCaseTypes,
      selectedSupplierIDs,
      selectedCustomerNames,
      selectedFromCountyIDs,
      selectedToCountyIDs,
      groupingLevel,
      excludeCompanyNameContains,
    },
  } = useDashboardControls();

  const startDate = daterange[0];
  const endDate = daterange[1];

  const isDateRangeTooLong =
    !!startDate &&
    !!endDate &&
    endDate.getTime() - startDate.getTime() > maxManagementReportDateRangeMs;

  const isReady = !!startDate && !!endDate && !isDateRangeTooLong;

  return {
    isReady,
    isDateRangeTooLong,
    groupingLevel: groupingLevel ?? GroupingLevel.Day,
    /**
     * Skickas som request-body till KPI-endpointen. Länsfiltren läggs på måttets egen
     * länsbetydelse (kundens län, hemlänet, uppdragets från/till-län) — se OperationsKpiLogic.
     */
    filter: {
      startDate: startDate as Date,
      endDate: endDate as Date,
      caseTypes: selectedCaseTypes,
      supplierIDs: selectedSupplierIDs,
      customerNames: selectedCustomerNames,
      fromCountyIDs: selectedFromCountyIDs,
      toCountyIDs: selectedToCountyIDs,
      groupBy: groupingLevel,
      excludeCompanyNameContains: excludeCompanyNameContains ?? undefined,
    },
    /** Beroendelista för useEffect — primitiva värden, så effekten inte loopar. */
    deps: [
      startDate ? startDate.getTime() : null,
      endDate ? endDate.getTime() : null,
      selectedCaseTypes,
      selectedSupplierIDs,
      selectedCustomerNames,
      selectedFromCountyIDs,
      selectedToCountyIDs,
      groupingLevel,
      excludeCompanyNameContains,
      isDateRangeTooLong,
    ] as const,
  };
};

export default useOperationsKpiFilter;
